import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import {
  ArrowLeft, BookOpen, PlayCircle, RotateCcw, Clock, Flag, Check, X,
  ChevronLeft, ChevronRight, CheckCircle2, Minus,
} from 'lucide-react';
import { Button, Modal, Spinner } from '../../components/ui';

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const MockTestRoom = () => {
  const { t } = useTranslation();
  const { examId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [flagged, setFlagged] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // null = not submitted
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const timeLeftRef = useRef(0);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.post(`/mock-tests/${examId}/start`);
        const { exam: e, questions: qs } = res.data.data;
        setExam(e);
        setQuestions(qs);
        setTimeLeft(e.duration * 60);
        timeLeftRef.current = e.duration * 60;
      } catch (err) {
        toast.error(err.response?.data?.message || t('mockTest.toasts.loadFailed'));
        navigate('/student/dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [examId]);

  // Countdown timer
  useEffect(() => {
    if (!started || result) return;
    const tick = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        timeLeftRef.current = next;
        if (next <= 0) {
          clearInterval(tick);
          handleSubmit(true);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [started, result]);

  const handleSubmit = useCallback(async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    setShowSubmitModal(false);
    try {
      const answersArr = Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer }));
      const res = await api.post(`/mock-tests/${examId}/submit`, { answers: answersArr });
      setResult(res.data.data);
      if (auto) toast(t('mockTest.toasts.timeUp'), { icon: '⏱' });
    } catch (err) {
      toast.error(err.response?.data?.message || t('mockTest.toasts.submitFailed'));
      setSubmitting(false);
    }
  }, [answers, examId, submitting, t]);

  const currentQuestion = questions[currentIdx];

  const getStatus = (q, idx) => {
    const qId = q._id || q.id;
    if (idx === currentIdx) return 'current';
    if (flagged.has(qId)) return 'flagged';
    const a = answers[qId];
    if (a !== undefined && a !== '' && !(Array.isArray(a) && a.length === 0)) return 'answered';
    return 'unanswered';
  };

  const answeredCount = questions.filter((q) => {
    const qId = q._id || q.id;
    const a = answers[qId];
    return a !== undefined && a !== '' && !(Array.isArray(a) && a.length === 0);
  }).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" className="text-themePrimary" />
          <p className="text-slate-400 text-sm">{t('mockTest.loading')}</p>
        </div>
      </div>
    );
  }

  // ── RESULTS VIEW ──────────────────────────────────────────────────────────
  if (result) {
    const pct = result.percentage;
    const scoreColor = pct >= 75 ? 'text-green-400' : pct >= 50 ? 'text-yellow-400' : 'text-red-400';
    const ringColor = pct >= 75 ? 'stroke-green-400' : pct >= 50 ? 'stroke-yellow-400' : 'stroke-red-400';

    return (
      <div className="min-h-screen bg-slate-950 text-white overflow-y-auto">
        {/* Header */}
        <div className="bg-themePrimary px-6 py-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <span className="text-white font-bold">{exam?.name}</span>
            <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">{t('mockTest.results.badge')}</span>
          </div>
          <button
            onClick={() => navigate('/student/dashboard')}
            className="text-white/70 hover:text-white text-sm flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('mockTest.results.backToDashboard')}
          </button>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
          {/* Score card */}
          <div className="bg-slate-900 rounded-2xl p-8 border border-slate-700 flex flex-col sm:flex-row items-center gap-8">
            {/* Ring */}
            <div className="relative flex-shrink-0 w-36 h-36">
              <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#1e293b" strokeWidth="10" />
                <circle
                  cx="60" cy="60" r="52" fill="none" strokeWidth="10"
                  strokeDasharray={`${2 * Math.PI * 52}`}
                  strokeDashoffset={`${2 * Math.PI * 52 * (1 - pct / 100)}`}
                  strokeLinecap="round"
                  className={`transition-all duration-1000 ${ringColor}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-3xl font-bold ${scoreColor}`}>{pct}%</span>
              </div>
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-2xl font-bold text-white mb-1">
                {pct >= 75 ? t('mockTest.results.excellent') : pct >= 50 ? t('mockTest.results.good') : t('mockTest.results.keepPracticing')}
              </h2>
              <p className="text-slate-400 mb-4">{t('mockTest.results.scoreSummary', { score: result.score, total: result.totalMarks })}</p>
              <div className="flex flex-wrap gap-3">
                <div className="bg-green-900/40 border border-green-700 rounded-lg px-4 py-2 text-center">
                  <div className="text-green-400 font-bold text-lg">{result.results.filter(r => r.isCorrect === true).length}</div>
                  <div className="text-green-300 text-xs">{t('mockTest.results.correct')}</div>
                </div>
                <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-2 text-center">
                  <div className="text-red-400 font-bold text-lg">{result.results.filter(r => r.isCorrect === false).length}</div>
                  <div className="text-red-300 text-xs">{t('mockTest.results.wrong')}</div>
                </div>
                <div className="bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-center">
                  <div className="text-slate-300 font-bold text-lg">{result.results.filter(r => r.isCorrect === null || (r.studentAnswer === undefined || r.studentAnswer === '' || r.studentAnswer === null)).length}</div>
                  <div className="text-slate-400 text-xs">{t('mockTest.results.skipped')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Retake button */}
          <div className="flex gap-3">
            <Button
              variant="primary"
              icon={RotateCcw}
              onClick={() => { setResult(null); setAnswers({}); setCurrentIdx(0); setFlagged(new Set()); setStarted(false); setSubmitting(false); setTimeLeft(exam.duration * 60); }}
            >
              {t('mockTest.results.retakeTest')}
            </Button>
            <Button variant="outline" tone="dark" onClick={() => navigate('/student/dashboard')}>
              {t('mockTest.results.backToDashboard')}
            </Button>
          </div>

          {/* Question-by-question review */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-200">{t('mockTest.results.answerReview')}</h3>
            {result.results.map((r, idx) => {
              const isSkipped = r.studentAnswer === undefined || r.studentAnswer === null || r.studentAnswer === '';
              const bg = r.isCorrect === true ? 'border-green-700 bg-green-900/20' : r.isCorrect === false ? 'border-red-700 bg-red-900/20' : 'border-slate-700 bg-slate-800/40';
              const icon = r.isCorrect === true ? (
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
              ) : r.isCorrect === false ? (
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <X className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
              ) : (
                <div className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Minus className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
              );

              return (
                <div key={r.questionId} className={`rounded-xl border p-5 ${bg}`}>
                  <div className="flex items-start gap-3 mb-3">
                    {icon}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-400 font-medium">{t('mockTest.results.question', { num: idx + 1 })}</span>
                        <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{t('mockTest.results.marks', { count: r.marks })}</span>
                        {r.marksObtained !== 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded ${r.marksObtained > 0 ? 'bg-green-800 text-green-300' : 'bg-red-800 text-red-300'}`}>
                            {r.marksObtained > 0 ? '+' : ''}{r.marksObtained}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-100 text-sm font-medium">{r.questionText}</p>
                    </div>
                  </div>

                  {r.type !== 'DESCRIPTIVE' && r.options && (
                    <div className="space-y-1.5 mt-3 ml-9">
                      {r.options.map((opt) => {
                        const optId = opt._id?.toString();
                        const isCorrectOpt = r.type === 'MULTIPLE_SELECT'
                          ? Array.isArray(r.correctAnswer) && r.correctAnswer.includes(optId)
                          : optId === r.correctAnswer;
                        const isStudentOpt = Array.isArray(r.studentAnswer)
                          ? r.studentAnswer.map(String).includes(optId)
                          : String(r.studentAnswer) === optId;
                        const optBg = isCorrectOpt
                          ? 'bg-green-800/60 border-green-600 text-green-200'
                          : isStudentOpt && !isCorrectOpt
                          ? 'bg-red-800/60 border-red-600 text-red-200'
                          : 'bg-slate-800 border-slate-600 text-slate-400';
                        return (
                          <div key={optId} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs ${optBg}`}>
                            {isCorrectOpt && <Check className="w-3 h-3 text-green-400 flex-shrink-0" strokeWidth={3} />}
                            {isStudentOpt && !isCorrectOpt && <X className="w-3 h-3 text-red-400 flex-shrink-0" strokeWidth={3} />}
                            {!isCorrectOpt && !isStudentOpt && <span className="w-3 flex-shrink-0"/>}
                            {opt.text}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {r.type === 'DESCRIPTIVE' && (
                    <div className="ml-9 mt-2 space-y-1.5">
                      <div className="text-xs text-slate-400">{t('mockTest.results.yourAnswer')} <span className="text-slate-200">{r.studentAnswer || <em>{t('mockTest.results.notAnswered')}</em>}</span></div>
                      {r.correctAnswer && <div className="text-xs text-green-400">{t('mockTest.results.modelAnswer')} <span className="text-green-200">{r.correctAnswer}</span></div>}
                    </div>
                  )}

                  {r.type === 'TRUE_FALSE' && (
                    <div className="ml-9 mt-2 space-y-1 text-xs">
                      <div className="text-slate-400">{t('mockTest.results.yourAnswer')} <span className={r.isCorrect ? 'text-green-300' : 'text-red-300'}>{String(r.studentAnswer)}</span></div>
                      <div className="text-slate-400">{t('mockTest.results.correctAnswerLabel')} <span className="text-green-300">{String(r.correctAnswer)}</span></div>
                    </div>
                  )}

                  {isSkipped && r.type !== 'DESCRIPTIVE' && (
                    <p className="ml-9 mt-2 text-xs text-slate-500 italic">{t('mockTest.results.notAttempted')}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── START SCREEN ──────────────────────────────────────────────────────────
  if (!started) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-themePrimary/20 border border-themePrimary/40 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <BookOpen className="w-8 h-8 text-themePrimary" />
          </div>
          <span className="inline-block bg-themePrimary/10 text-themePrimary text-xs font-medium px-3 py-1 rounded-full mb-3">{t('mockTest.start.badge')}</span>
          <h2 className="text-2xl font-bold text-white mb-2">{exam?.name}</h2>
          {exam?.description && <p className="text-slate-400 text-sm mb-5">{exam.description}</p>}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-slate-800 rounded-xl p-3">
              <div className="text-slate-400 text-xs mb-1">{t('mockTest.start.duration')}</div>
              <div className="text-white font-semibold">{t('mockTest.start.durationValue', { count: exam?.duration })}</div>
            </div>
            <div className="bg-slate-800 rounded-xl p-3">
              <div className="text-slate-400 text-xs mb-1">{t('mockTest.start.questions')}</div>
              <div className="text-white font-semibold">{questions.length}</div>
            </div>
          </div>
          <div className="bg-themePrimary/10 border border-themePrimary/30 rounded-xl p-4 mb-6 text-left space-y-1.5 text-xs text-themePrimary/90">
            <p>• {t('mockTest.start.noteNoProctoring')}</p>
            <p>• {t('mockTest.start.noteResultsShown')}</p>
            <p>• {t('mockTest.start.noteUnlimitedRetakes')}</p>
          </div>
          <Button variant="primary" fullWidth size="lg" icon={PlayCircle} onClick={() => setStarted(true)}>
            {t('mockTest.start.startButton')}
          </Button>
          <button
            onClick={() => navigate('/student/dashboard')}
            className="mt-3 w-full text-slate-500 hover:text-slate-300 text-sm py-2"
          >
            {t('mockTest.start.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // ── EXAM VIEW ─────────────────────────────────────────────────────────────
  // Standardized to the light theme to match ExamRoom's Q&A view — the two
  // "take an exam" experiences should look the same during active answering.
  const navDotColors = {
    current: 'bg-themePrimary text-white ring-2 ring-themePrimary/30',
    answered: 'bg-green-500 text-white',
    flagged: 'bg-yellow-400 text-white',
    unanswered: 'bg-slate-100 text-slate-600 hover:bg-slate-200',
  };

  const legendItems = [
    { color: 'bg-green-500', label: t('mockTest.exam.legendAnswered') },
    { color: 'bg-yellow-400', label: t('mockTest.exam.legendFlagged') },
    { color: 'bg-themePrimary', label: t('mockTest.exam.legendCurrent') },
    { color: 'bg-slate-200', label: t('mockTest.exam.legendNotVisited') },
  ];

  return (
    <div className="fixed inset-0 bg-slate-100 flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs bg-themePrimary/10 text-themePrimary px-2 py-1 rounded-full font-medium whitespace-nowrap">{t('mockTest.exam.practiceLabel')}</span>
          <span className="text-sm font-semibold text-slate-800 truncate">{exam?.name}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-1.5 text-sm font-mono tabular-nums font-bold ${timeLeft < 120 ? 'text-red-600' : 'text-themePrimary'}`}>
            <Clock className="w-4 h-4" />
            {formatTime(timeLeft)}
          </div>
          <div className="text-xs text-slate-500">{t('mockTest.exam.answeredCount', { answered: answeredCount, total: questions.length })}</div>
          <Button size="sm" className="!bg-green-600 hover:!bg-green-700" onClick={() => setShowSubmitModal(true)}>
            {t('mockTest.exam.submit')}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Question navigator sidebar */}
        <div className="w-48 flex-shrink-0 bg-white border-r border-slate-200 overflow-y-auto p-3">
          <p className="text-xs text-slate-500 font-medium mb-2">{t('mockTest.exam.questionsNav')}</p>
          <div className="grid grid-cols-4 gap-1.5">
            {questions.map((q, idx) => {
              const st = getStatus(q, idx);
              return (
                <button
                  key={q._id || q.id}
                  onClick={() => setCurrentIdx(idx)}
                  className={`w-9 h-9 rounded-lg text-xs font-semibold transition-colors ${navDotColors[st]}`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-1.5 text-xs">
            {legendItems.map(({ color, label }) => (
              <div key={label} className="flex items-center gap-2 text-slate-500">
                <div className={`w-3 h-3 rounded-sm ${color}`} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Question area */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentQuestion && (() => {
            const qId = currentQuestion._id || currentQuestion.id;
            const currentAnswer = answers[qId];

            return (
              <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-slate-500 font-medium">
                    {t('mockTest.exam.questionOf', { current: currentIdx + 1, total: questions.length })}
                    {currentQuestion.marks > 1 && ` ${t('mockTest.exam.marksSuffix', { count: currentQuestion.marks })}`}
                  </span>
                  <button
                    onClick={() => {
                      setFlagged((prev) => {
                        const next = new Set(prev);
                        if (next.has(qId)) next.delete(qId);
                        else next.add(qId);
                        return next;
                      });
                    }}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                      flagged.has(qId) ? 'border-yellow-300 bg-yellow-50 text-yellow-700' : 'border-slate-300 text-slate-600 hover:border-yellow-300 hover:text-yellow-600'
                    }`}
                  >
                    <Flag className="w-3.5 h-3.5" fill={flagged.has(qId) ? 'currentColor' : 'none'} />
                    {flagged.has(qId) ? t('mockTest.exam.flagged') : t('mockTest.exam.flag')}
                  </button>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-5">
                  <p className="text-slate-900 text-base leading-relaxed">{currentQuestion.text}</p>
                </div>

                {/* MCQ / SINGLE_CHOICE */}
                {(currentQuestion.type === 'MCQ' || currentQuestion.type === 'SINGLE_CHOICE') && (
                  <div className="space-y-2.5">
                    {currentQuestion.options.map((opt) => {
                      const optId = opt._id?.toString();
                      const isSelected = String(currentAnswer) === optId;
                      return (
                        <button
                          key={optId}
                          onClick={() => setAnswers((prev) => ({ ...prev, [qId]: optId }))}
                          className={`w-full text-left px-4 py-3.5 rounded-xl border-2 text-sm transition-all ${
                            isSelected
                              ? 'border-themePrimary bg-themePrimary/5 text-slate-900'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-themePrimary/40 hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-themePrimary' : 'border-slate-300'}`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-themePrimary" />}
                            </div>
                            {opt.text}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* MULTIPLE_SELECT */}
                {currentQuestion.type === 'MULTIPLE_SELECT' && (
                  <div className="space-y-2.5">
                    <p className="text-xs text-slate-500 mb-2">{t('mockTest.exam.selectAllThatApply')}</p>
                    {currentQuestion.options.map((opt) => {
                      const optId = opt._id?.toString();
                      const selected = Array.isArray(currentAnswer) ? currentAnswer.includes(optId) : false;
                      return (
                        <button
                          key={optId}
                          onClick={() => {
                            const cur = Array.isArray(currentAnswer) ? currentAnswer : [];
                            const next = selected ? cur.filter((id) => id !== optId) : [...cur, optId];
                            setAnswers((prev) => ({ ...prev, [qId]: next }));
                          }}
                          className={`w-full text-left px-4 py-3.5 rounded-xl border-2 text-sm transition-all ${
                            selected ? 'border-themePrimary bg-themePrimary/5 text-slate-900' : 'border-slate-200 bg-white text-slate-700 hover:border-themePrimary/40 hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${selected ? 'border-themePrimary bg-themePrimary' : 'border-slate-300'}`}>
                              {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                            </div>
                            {opt.text}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* TRUE_FALSE */}
                {currentQuestion.type === 'TRUE_FALSE' && (
                  <div className="grid grid-cols-2 gap-3">
                    {['True', 'False'].map((val) => {
                      const isSelected = String(currentAnswer).toLowerCase() === val.toLowerCase();
                      return (
                        <button
                          key={val}
                          onClick={() => setAnswers((prev) => ({ ...prev, [qId]: val }))}
                          className={`py-4 rounded-xl border-2 text-sm font-semibold transition-all ${
                            isSelected ? 'border-themePrimary bg-themePrimary/5 text-slate-900' : 'border-slate-200 bg-white text-slate-600 hover:border-themePrimary/40'
                          }`}
                        >
                          {val === 'True' ? t('mockTest.exam.true') : t('mockTest.exam.false')}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* DESCRIPTIVE */}
                {currentQuestion.type === 'DESCRIPTIVE' && (
                  <textarea
                    value={currentAnswer || ''}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [qId]: e.target.value }))}
                    rows={6}
                    placeholder={t('mockTest.exam.descriptivePlaceholder')}
                    className="w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-themePrimary resize-none"
                  />
                )}

                {/* Navigation */}
                <div className="flex justify-between mt-6">
                  <button
                    onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                    disabled={currentIdx === 0}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-sm transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t('mockTest.exam.previous')}
                  </button>
                  {currentIdx < questions.length - 1 ? (
                    <Button variant="primary" icon={ChevronRight} iconPosition="right" onClick={() => setCurrentIdx((i) => Math.min(questions.length - 1, i + 1))}>
                      {t('mockTest.exam.next')}
                    </Button>
                  ) : (
                    <Button icon={CheckCircle2} className="!bg-green-600 hover:!bg-green-700" onClick={() => setShowSubmitModal(true)}>
                      {t('mockTest.exam.submitTest')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Submit confirmation modal */}
      <Modal
        open={showSubmitModal}
        onClose={() => { if (!submitting) setShowSubmitModal(false); }}
        title={t('mockTest.submitModal.title')}
        size="sm"
        footer={
          <>
            <Button variant="outline" fullWidth onClick={() => setShowSubmitModal(false)} disabled={submitting}>
              {t('mockTest.submitModal.continue')}
            </Button>
            <Button fullWidth className="!bg-green-600 hover:!bg-green-700" loading={submitting} onClick={() => handleSubmit(false)}>
              {submitting ? t('mockTest.submitModal.submitting') : t('mockTest.submitModal.submitAndSeeResults')}
            </Button>
          </>
        }
      >
        <p className="text-slate-600 text-sm">
          {t('mockTest.submitModal.answeredSummary', { answered: answeredCount, total: questions.length })}
        </p>
        {answeredCount < questions.length && (
          <p className="text-amber-600 text-sm mt-1">{t('mockTest.submitModal.unanswered', { count: questions.length - answeredCount })}</p>
        )}
      </Modal>
    </div>
  );
};

export default MockTestRoom;
